/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { Button, CircularProgress, MenuItem, Paper, Select, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { withStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import * as React from 'react';
import { connect } from 'react-redux';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { getAllRoles, getAllServiceLevels } from '../common/common';
import { editUserEndPoint, useradminRoute } from '../common/constants';
import { loadFromLocalStorage } from '../common/localStorage';
import type { UserRole } from '../common/UserModel';
import { actionCreators } from '../store/User';
import DownshiftMultiple from './Downshift';
import Error from './Error';

interface Props {
    classes?: any
    selectedUser: any
    getUser: Function
    isLoading: boolean
}

const styles = (_theme: Theme) => ({
    paper: {
        padding: 20,
        //width:`calc(50vw)`
    },
    data: {
        marginTop: '10px',
        marginBottom: '10px'
    },
    save: {
        marginTop: '50px',
        marginRight: '10px',
        backgroundColor: '#ffbe00'
    },
    cancel: {
        marginTop: '50px',
        border: '1px solid #ffbe00'
    },
    link: {
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'none'
        }
    },
    progressWrap: {
        textAlign: 'center' as const,
        marginTop: '20vh'
    },
    buttonProgress: {
        marginTop: -33,
        marginLeft: 17,
    },
    selectItem: {
        textAlign: 'center' as const
    }
});

const displayFields = [
    { key: 'username', title: 'Username' },
    { key: 'company', title: 'Company' },
    { key: 'email', title: 'Email' },
    { key: 'usertype', title: 'User Type' }
]
function renderData(classes: any, key: any, title: any, user: any) {
    return (
        <div className={classes.data} key={key}>
            <Grid container spacing={8}>
                <Grid>
                    <Typography className={classes.title} color="textSecondary" gutterBottom>
                        {title}
                    </Typography>
                    <Typography>
                        {user[key]}
                    </Typography>
                </Grid>
            </Grid>
        </div>
    );
}

interface ComponentState {
    allRoles: any[];
    initialRoles: string[];
    initialRoleIds: number[];
    selectedRoles: number[];
    selectedServiceLevel: number;
    allServiceLevels: any[];
    validationError: string;
    isErrorOpen: boolean;
    saveInProgress: boolean;
}

const UserEdit: React.FC<Props> = (props) => {
    const { classes, selectedUser, getUser, isLoading } = props;
    const { userid } = useParams<{ userid: string }>();
    const navigate = useNavigate();

    const [state, setState] = React.useState<ComponentState>({
        allRoles: [],
        initialRoles: [],
        initialRoleIds: [],
        selectedRoles: [],
        selectedServiceLevel: 0,
        allServiceLevels: [],
        validationError: '',
        isErrorOpen: false,
        saveInProgress: false
    });

    const fetchRoles = React.useCallback(async () => {
        if (selectedUser === undefined || selectedUser == null) {
            if (userid && parseInt(userid)) {
                getUser(userid);
            } else {
                navigate(useradminRoute);
            }
            return;
        }
        if (selectedUser === 'nouser') {
            navigate(useradminRoute);
        }

        let allRoles: any = loadFromLocalStorage('AllRoles');
        if (allRoles == null || allRoles === undefined) {
            allRoles = await getAllRoles();
        }
        if (selectedUser !== undefined) {
            const allowedRoles = allRoles.filter((x: any) => (selectedUser.usertype === 'External' ? !x.isInternalRole : true));
            setState(s => ({ ...s, allRoles: allowedRoles }));
        }
    }, [selectedUser, getUser, userid, navigate]);

    const fetchServiceLevels = React.useCallback(async () => {
        let allServiceLevels: any = loadFromLocalStorage('All ServiceLevels');
        if (allServiceLevels == null || allServiceLevels === undefined) {
            allServiceLevels = await getAllServiceLevels();
        }
        setState(s => ({ ...s, allServiceLevels }));
        if (selectedUser != null && selectedUser !== undefined) {
            setState(s => ({ ...s, selectedServiceLevel: selectedUser.serviceLevel }));
        }
    }, [selectedUser]);

    React.useEffect(() => {
        fetchRoles();
        fetchServiceLevels();
    }, [fetchRoles, fetchServiceLevels]);

    React.useEffect(() => {
        if (selectedUser && state.allRoles.length > 0) {
            const roleids: string = selectedUser['roleids'];
            if (roleids !== '') {
                const roleidstring = roleids.split(',') as any;
                const initialRolesIds: any = roleidstring.map((x: any) => {
                    return parseInt(x);
                });
                const initialRoles: Array<string> = [];
                state.allRoles.forEach((userrole: UserRole) => {
                    initialRolesIds.map((id: string) => {
                        if (userrole.roleid === parseInt(id)) {
                            initialRoles.push(userrole.rolename);
                        }
                    });
                });
                setState(s => ({ ...s, initialRoles: initialRoles, initialRoleIds: initialRolesIds, selectedRoles: initialRolesIds }));
            }
        }
    }, [selectedUser, state.allRoles]);

    const changeUserRoles = (selectedItem: any) => {
        const addedRoles: any = [];
        selectedItem.map((item: any) => {
            const selectedRole: UserRole | undefined = state.allRoles.find((role: UserRole) => {
                return role.rolename === item;
            });
            if (selectedRole !== undefined) {
                const t = selectedRole as UserRole;
                if (addedRoles.indexOf(t.roleid) === -1) {
                    addedRoles.push(t.roleid);
                }
            }
        });
        setState(s => ({ ...s, selectedRoles: addedRoles }));
    };

    const handleSave = () => {
        const deletedRoles: any = [];
        state.initialRoleIds.forEach((initialRoleId: any) => {
            if (state.selectedRoles !== undefined && state.selectedRoles.indexOf(initialRoleId as never) === -1) {
                deletedRoles.push(initialRoleId);
            }
        });
        const addedRoles: any = state.selectedRoles.filter((x: number) => {
            return deletedRoles.indexOf(x) === -1;
        });
        sendEditUserRequest(addedRoles, deletedRoles);
    };

    const sendEditUserRequest = (addedRoles: any, deletedRoles: any): any => {
        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
            'Content-Type': 'application/json'
        };
        const requestBody = {
            userid: selectedUser['id'],
            addedroles: `${addedRoles.join(';')}`,
            removedroles: `${deletedRoles.join(';')}`,
            serviceLevel: state.selectedServiceLevel
        };
        setState(s => ({ ...s, validationError: '', isErrorOpen: false, saveInProgress: true }));
        try {
            if (navigator.onLine) {
                fetch(editUserEndPoint, {
                    headers: new Headers(headerDict),
                    body: JSON.stringify(requestBody),
                    method: 'POST'
                })
                    .then(async (response: any) => {
                        const reader = response.body.getReader();
                        let responsetext = '';
                        await reader.read().then((x: any) => {
                            responsetext = new TextDecoder('utf-8').decode(x.value);
                        });
                        switch (response.status) {
                            case 200:
                                setState(s => ({ ...s, validationError: '', isErrorOpen: false, saveInProgress: false }));
                                navigate(useradminRoute);
                                break;
                            case 400:
                                setState(s => ({ ...s, validationError: responsetext, isErrorOpen: true, saveInProgress: false }));
                                break;
                            default:
                                setState(s => ({ ...s, validationError: '', isErrorOpen: true, saveInProgress: false }));
                        }
                    });
            }
        }
        catch (e) { }
    }
    const changeServiceLevel = (event: any) => {
        setState(s => ({ ...s, selectedServiceLevel: parseInt(event.target.value) }));
    };

    const radioList = (items: any, key: any, value: any) => {
        return (
            <div className={classes.data} key={'servicelevel'}>
                <Grid container spacing={8}>
                <Grid>
                        <Typography className={classes.title} color="textSecondary" gutterBottom>
                            Service Level
                        </Typography>
                        <Select
                            id="serviceLevel"
                            value={`${state.selectedServiceLevel}`}
                            onChange={changeServiceLevel}
                            className={classes.selectItem}
                        >{
                                items.map((item: any) => {
                                    return <MenuItem value={`${item[value]}`} key={item[key]}>{`${item[value]}`}</MenuItem>;
                                })
                            }
                        </Select>
                    </Grid>
                </Grid>
            </div>
        );
    };

    return (
        isLoading ?
            <div className={classes.progressWrap}>
                <CircularProgress className={classes.progress} color="secondary" />
            </div> :
            <div>
                {selectedUser != null && <Paper className={classes.paper}>
                    <form>
                        {displayFields.map((field: any) => { return renderData(classes, field.key, field.title, selectedUser); })}
                        <Grid container>
                            <Grid>
                                <DownshiftMultiple label={`User Roles`} classes={classes} values={state.allRoles} id={`roleid`} value={`rolename`}
                                    initialValue={state.initialRoles} updateSelection={changeUserRoles} />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid>
                                {state.allServiceLevels && radioList(state.allServiceLevels, 'serviceLevelId', 'serviceLevelValue')}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid>
                                <Button disabled={state.saveInProgress || !navigator.onLine} className={classes.save} onClick={handleSave}>Save</Button>
                                {state.saveInProgress && <div className={classes.buttonProgress}><CircularProgress size={24} /></div>}
                            </Grid>
                            <Grid>
                                <NavLink className={classes.link} to={useradminRoute}><Button disabled={state.saveInProgress}
                                    className={classes.cancel}>Cancel</Button></NavLink>
                            </Grid>
                        </Grid>
                        <Error errorMessage={state.validationError} isopen={state.isErrorOpen} />
                    </form>
                </Paper>}
            </div>
    );
}

const mapStateToProps = (state: any) => ({
    selectedUser: state.user.selectedUser,
    isLoading: state.user.isLoading
});
const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);

export default connect(mapStateToProps, mapDispatchtoProps)(withStyles(styles)(UserEdit));